import { FocusableElement, Node } from '@react-types/shared'
import React, { ComponentType, DOMAttributes, ReactNode, ReactElement } from 'react'
import { Box, Flex, Text } from '../../../core-components'
import { EndContentProvider } from '../../data'
import { DefaultSelectedText } from './DefaultSelectedText'
import { SelectedText, TriggerStartIcon } from './StyledSelectTrigger'
import { DynamicItem } from './dynamicItemUtils'

export const BaseSelectValue = ({
  startIcon,
  valueProps,
  hasLabelInside,
  isRequired,
  labelProps,
  label,
  rootValueNode,
  selectedText = DefaultSelectedText,
  valuePath,
  value,
  placeholder,
  onLeafEndContent,
  size = 'regular',
}: {
  hasLabelInside: boolean
  isRequired?: boolean
  label: ReactNode
  labelProps: DOMAttributes<FocusableElement>
  placeholder: ReactNode
  rootValueNode: Node<DynamicItem> | null | undefined
  selectedText?: (
    selectedItem: Node<DynamicItem>,
    valuePath: readonly string[] | undefined,
    value?: string,
    onLeafEndContent?: (endContent: ReactNode | undefined) => void,
    size?: 'regular' | 'large'
  ) => ReactElement
  startIcon?: ComponentType
  value?: string
  valuePath?: readonly string[]
  valueProps: DOMAttributes<FocusableElement>
  onLeafEndContent?: (endContent: ReactNode | undefined) => void
  size?: 'regular' | 'large'
}) => {
  'use no memo'
  return (
    <Flex alignItems='center' gap={3} flexGrow={1} minWidth={0}>
      {startIcon && <TriggerStartIcon icon={startIcon} color='fg/major' w={4} h={4} />}
      <EndContentProvider value={{ iconColor: 'fg/minor', size: 3 }}>
        <Box {...valueProps} minWidth={0} mr={3} flexGrow={1}>
          {hasLabelInside && (
            <Flex
              display='flex'
              gap={3}
              alignItems='start'
              {...labelProps}
              style={{ pointerEvents: 'none' }}
            >
              <Text mb={2} display='block' textStyle='form-label-inline' color='fg/moderate'>
                {label}
              </Text>
              {isRequired && (
                <Text textStyle='form-label-inline' color='fg/caution'>
                  *
                </Text>
              )}
            </Flex>
          )}
          {rootValueNode ? (
            selectedText(rootValueNode, valuePath, value, onLeafEndContent, size)
          ) : (
            <BaseSelectPlaceholder placeholder={placeholder} size={size} />
          )}
        </Box>
      </EndContentProvider>
    </Flex>
  )
}

const BaseSelectPlaceholder = ({
  placeholder,
  size = 'regular',
}: {
  placeholder: React.ReactNode
  size?: 'regular' | 'large'
}) => {
  return typeof placeholder === 'string' ? (
    <SelectedText
      display='block'
      color='fg/minor'
      textStyle={size === 'large' ? 'form-value-l' : 'form-value'}
    >
      {placeholder}
    </SelectedText>
  ) : (
    <>{placeholder}</>
  )
}
